import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Smartphone as PhoneIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  AddBox as AddIcon
} from '@mui/icons-material';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Detect if app is already installed/in standalone mode
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone ||
                        document.referrer.includes('android-app://');
      setIsStandalone(standalone);
    };

    // Detect platform
    const detectPlatform = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      const isAndroidDevice = /android/.test(userAgent);
      
      setIsIOS(isIOSDevice);
      setIsAndroid(isAndroidDevice);
    };

    checkStandalone();
    detectPlatform();

    // Listen for the beforeinstallprompt event (Android)
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only show prompt if not already installed and not shown in this session
      if (!isStandalone && !sessionStorage.getItem('pwa-prompt-dismissed')) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000); // Show after 3 seconds
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setSnackbar({
        open: true,
        message: 'App installed successfully!',
        severity: 'success'
      });
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check for iOS install prompt
    if (isIOS && !isStandalone && !sessionStorage.getItem('ios-prompt-dismissed')) {
      setTimeout(() => {
        setShowIOSPrompt(true);
      }, 5000); // Show after 5 seconds for iOS
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone, isIOS]);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) {
      setSnackbar({
        open: true,
        message: 'Installation not available. Please try again later.',
        severity: 'warning'
      });
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setSnackbar({
          open: true,
          message: 'App installation started...',
          severity: 'info'
        });
      } else {
        console.log('User dismissed the install prompt');
        setSnackbar({
          open: true,
          message: 'Installation cancelled',
          severity: 'info'
        });
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      sessionStorage.setItem('pwa-prompt-dismissed', 'true');
    } catch (error) {
      console.error('Error during installation:', error);
      setSnackbar({
        open: true,
        message: 'Installation failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDismiss = (promptType = 'android') => {
    if (promptType === 'ios') {
      setShowIOSPrompt(false);
      sessionStorage.setItem('ios-prompt-dismissed', 'true');
    } else {
      setShowInstallPrompt(false);
      sessionStorage.setItem('pwa-prompt-dismissed', 'true');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Don't show anything if already installed
  if (isStandalone) return null;

  return (
    <>
      {/* Android Install Prompt */}
      <Dialog
        open={showInstallPrompt}
        onClose={() => handleDismiss('android')}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            maxHeight: '90vh',
            margin: isSmallScreen ? '16px' : '32px',
            width: isSmallScreen ? 'calc(100% - 32px)' : 'auto',
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            px: isSmallScreen ? 2 : 3,
            pt: isSmallScreen ? 2 : 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AndroidIcon color="primary" sx={{ fontSize: isSmallScreen ? 20 : 24 }} />
            <Typography variant={isSmallScreen ? "subtitle1" : "h6"} fontWeight="bold">
              Install T-TRADES App
            </Typography>
          </Box>
          <IconButton onClick={() => handleDismiss('android')} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          sx={{ 
            pt: 2,
            px: isSmallScreen ? 2 : 3,
            pb: 1,
            overflow: 'auto'
          }}
        >
          <Stack spacing={isSmallScreen ? 2 : 3}>
            <Box sx={{ textAlign: 'center' }}>
              <PhoneIcon sx={{ fontSize: isSmallScreen ? 40 : 50, color: 'primary.main', mb: 1.5 }} />
              <Typography variant={isSmallScreen ? "body1" : "h6"} gutterBottom color="primary" fontWeight="bold">
                Get the full app experience!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.875rem' : '1rem' }}>
                Install T-TRADES on your device for faster access and native app features.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: isSmallScreen ? 1.5 : 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontSize: isSmallScreen ? '0.875rem' : '1rem' }}>
                App Features:
              </Typography>
              <Stack spacing={0.5}>
                {[
                  'Faster loading and performance',
                  'Offline access to portfolio',
                  'Push notifications',
                  'Native mobile experience'
                ].map((feature, index) => (
                  <Typography key={index} variant="body2" sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
                  }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    {feature}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          p: isSmallScreen ? 2 : 3, 
          pt: 1,
          gap: 1,
          flexDirection: isSmallScreen ? 'column' : 'row'
        }}>
          <Button 
            onClick={() => handleDismiss('android')} 
            color="inherit"
            fullWidth={isSmallScreen}
            sx={{ order: isSmallScreen ? 2 : 1 }}
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleAndroidInstall}
            variant="contained"
            startIcon={<DownloadIcon />}
            fullWidth={isSmallScreen}
            sx={{
              borderRadius: 2,
              px: 3,
              order: isSmallScreen ? 1 : 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              }
            }}
          >
            Install App
          </Button>
        </DialogActions>
      </Dialog>

      {/* iOS Install Prompt */}
      <Dialog
        open={showIOSPrompt}
        onClose={() => handleDismiss('ios')}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.divider}`,
            maxHeight: '90vh',
            margin: isSmallScreen ? '16px' : '32px',
            width: isSmallScreen ? 'calc(100% - 32px)' : 'auto',
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            px: isSmallScreen ? 2 : 3,
            pt: isSmallScreen ? 2 : 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AppleIcon color="primary" sx={{ fontSize: isSmallScreen ? 20 : 24 }} />
            <Typography variant={isSmallScreen ? "subtitle1" : "h6"} fontWeight="bold">
              Add to Home Screen
            </Typography>
          </Box>
          <IconButton onClick={() => handleDismiss('ios')} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          sx={{ 
            pt: 2,
            px: isSmallScreen ? 2 : 3,
            pb: 1,
            overflow: 'auto'
          }}
        >
          <Stack spacing={isSmallScreen ? 2 : 3}>
            <Box sx={{ textAlign: 'center' }}>
              <PhoneIcon sx={{ fontSize: isSmallScreen ? 40 : 50, color: 'primary.main', mb: 1.5 }} />
              <Typography variant={isSmallScreen ? "body1" : "h6"} gutterBottom color="primary" fontWeight="bold">
                Install T-TRADES on iOS
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.875rem' : '1rem' }}>
                Add T-TRADES to your home screen for a native app experience.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: isSmallScreen ? 1.5 : 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontSize: isSmallScreen ? '0.875rem' : '1rem' }}>
                Installation Steps:
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: isSmallScreen ? 24 : 28,
                      height: isSmallScreen ? 24 : 28,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    1
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: isSmallScreen ? '0.8rem' : '0.875rem' }}>
                      Tap the Share button <ShareIcon sx={{ fontSize: 14, mx: 0.5 }} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.7rem' : '0.75rem' }}>
                      In Safari's bottom toolbar
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ ml: isSmallScreen ? 3 : 4 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: isSmallScreen ? 24 : 28,
                      height: isSmallScreen ? 24 : 28,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    2
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: isSmallScreen ? '0.8rem' : '0.875rem' }}>
                      Select "Add to Home Screen" <AddIcon sx={{ fontSize: 14, mx: 0.5 }} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.7rem' : '0.75rem' }}>
                      Scroll down in the share menu
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ ml: isSmallScreen ? 3 : 4 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: isSmallScreen ? 24 : 28,
                      height: isSmallScreen ? 24 : 28,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    3
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: isSmallScreen ? '0.8rem' : '0.875rem' }}>
                      Tap "Add" to confirm
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.7rem' : '0.75rem' }}>
                      App will appear on your home screen
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: isSmallScreen ? 1.5 : 2,
                backgroundColor: `${theme.palette.success.main}15`,
                borderRadius: 2,
                border: `1px solid ${theme.palette.success.main}30`,
              }}
            >
              <Typography variant="body2" color="success.main" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: isSmallScreen ? '0.8rem' : '0.875rem'
              }}>
                <HomeIcon sx={{ fontSize: 16 }} />
                Access T-TRADES directly from your home screen like any other app!
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          p: isSmallScreen ? 2 : 3, 
          pt: 1,
          gap: 1,
          flexDirection: isSmallScreen ? 'column' : 'row'
        }}>
          <Button 
            onClick={() => handleDismiss('ios')} 
            color="inherit"
            fullWidth={isSmallScreen}
            sx={{ order: isSmallScreen ? 2 : 1 }}
          >
            Maybe Later
          </Button>
          <Button
            onClick={() => handleDismiss('ios')}
            variant="contained"
            fullWidth={isSmallScreen}
            sx={{
              borderRadius: 2,
              px: 3,
              order: isSmallScreen ? 1 : 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              }
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 10000 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt; 